/**
 * Cookie Consent Management Utility
 *
 * Manages user consent for cookies and tracking.
 * Supports three states: 'accepted', 'declined', 'unset'
 * Consent expires after 12 months and requires re-consent.
 *
 * GDPR/CCPA compliant:
 * - No tracking until explicit consent
 * - Clear opt-out mechanism
 * - Persistent storage of user preference
 * - Consent expiry with re-prompt
 */

const CONSENT_KEY = 'presage_cookie_consent';
const CONSENT_EXPIRY_MONTHS = 12;

/**
 * Consent states
 */
export const ConsentState = {
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  UNSET: 'unset',
};

/**
 * Get stored consent data from localStorage
 * Returns { status: string, timestamp: string } or null
 */
function getStoredConsent() {
  try {
    const data = localStorage.getItem(CONSENT_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to retrieve consent data:', error);
    return null;
  }
}

/**
 * Store consent data in localStorage
 */
function storeConsent(status) {
  const consentData = {
    status,
    timestamp: new Date().toISOString(),
  };

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    return true;
  } catch (error) {
    console.error('Failed to store consent data:', error);
    return false;
  }
}

/**
 * Check if consent has expired (12 months)
 */
export function isConsentExpired() {
  const consent = getStoredConsent();

  if (!consent || !consent.timestamp) {
    return false; // No consent = not expired, just unset
  }

  const consentDate = new Date(consent.timestamp);
  const expiryDate = new Date(consentDate);
  expiryDate.setMonth(expiryDate.getMonth() + CONSENT_EXPIRY_MONTHS);

  return new Date() > expiryDate;
}

/**
 * Get current consent status
 * Returns one of: ConsentState.ACCEPTED, ConsentState.DECLINED, ConsentState.UNSET
 */
export function getConsent() {
  const consent = getStoredConsent();

  // If expired, treat as unset
  if (consent && isConsentExpired()) {
    return ConsentState.UNSET;
  }

  return consent ? consent.status : ConsentState.UNSET;
}

/**
 * Check if user has accepted cookies
 * Returns true only if consent is explicitly accepted and not expired
 */
export function hasConsent() {
  return getConsent() === ConsentState.ACCEPTED;
}

/**
 * Set consent status (accepted or declined)
 * Also stores timestamp for expiry tracking
 */
export function setConsent(status) {
  if (
    status !== ConsentState.ACCEPTED &&
    status !== ConsentState.DECLINED
  ) {
    console.error('Invalid consent status:', status);
    return false;
  }

  return storeConsent(status);
}

/**
 * Accept cookies
 */
export function acceptConsent() {
  return setConsent(ConsentState.ACCEPTED);
}

/**
 * Decline cookies
 */
export function declineConsent() {
  return setConsent(ConsentState.DECLINED);
}

/**
 * Clear consent (reset to unset state)
 * Useful for testing or explicit reset
 */
export function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
    return true;
  } catch (error) {
    console.error('Failed to clear consent data:', error);
    return false;
  }
}

/**
 * Get consent timestamp
 * Returns ISO date string or null
 */
export function getConsentTimestamp() {
  const consent = getStoredConsent();
  return consent ? consent.timestamp : null;
}

/**
 * Get consent expiry date
 * Returns Date object or null
 */
export function getConsentExpiryDate() {
  const timestamp = getConsentTimestamp();

  if (!timestamp) {
    return null;
  }

  const consentDate = new Date(timestamp);
  const expiryDate = new Date(consentDate);
  expiryDate.setMonth(expiryDate.getMonth() + CONSENT_EXPIRY_MONTHS);

  return expiryDate;
}

/**
 * Check if user needs to be prompted for consent
 * Returns true if consent is unset or expired
 */
export function needsConsentPrompt() {
  const consent = getConsent();
  return consent === ConsentState.UNSET || isConsentExpired();
}
